<template>
  <div v-if="isEvent" class="message-event">
    <span>{{ message.content }}</span>
    <span class="message-event__time">{{ formatMessageTime(message.sent_at) }}</span>
  </div>

  <article v-else class="message" :class="{ 'is-internal': isInternal, 'is-compact': compact }">
    <StaffInitialsAvatar v-if="!compact && message.sender" :full-name="message.sender.full_name" />

    <div class="message__body">
      <div v-if="isInternal" class="message__internal-label"><Lock :size="11" /> Internal note</div>

      <header class="message__header">
        <div class="tw-min-w-0">
          <span class="message__sender">{{ message.sender?.full_name ?? "Unknown" }}</span>
          <span v-if="senderRole" class="message__role">, {{ senderRole }}</span>
          <div v-if="!compact && !isInternal" class="message__recipient">to {{ recipient }}</div>
        </div>
        <time class="message__time" :datetime="message.sent_at">{{ formatMessageTime(message.sent_at) }}</time>
      </header>

      <p class="message__content">
        <template v-for="(segment, index) in contentSegments" :key="index">
          <span v-if="segment.mention" class="message__mention">@{{ segment.text }}</span>
          <template v-else>{{ segment.text }}</template>
        </template>
      </p>

      <ul v-if="message.attachments.length" class="message__attachments">
        <li v-for="attachment in message.attachments" :key="attachment.id">
          <MessageAttachment :attachment="attachment" />
        </li>
      </ul>

      <div v-if="message.read_by_patient && !isInternal" class="message__read"><Check :size="12" /> Read</div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { Check, Lock } from "lucide-vue-next"
import {
  PatientConversationMessageDTO,
  PatientConversationMessageKindEnum,
  PatientConversationMessageVisibilityEnum,
  PatientConversationRoleEnum
} from "@vitable/asclepius-api-client-ts"
import StaffInitialsAvatar from "@/modules/patient-conversations/components/StaffInitialsAvatar.vue"
import MessageAttachment from "@/modules/patient-conversations/components/thread/MessageAttachment.vue"
import { roleLabel } from "@/modules/patient-conversations/constants/patient-conversation-labels"
import { formatMessageTime } from "@/modules/patient-conversations/utils/patient-conversation-format"

const props = defineProps<{
  message: PatientConversationMessageDTO
  /** The patient's display name, for the "to …" line under a staff message. */
  patientName: string
  /** The floating panel: no avatars or recipient line. */
  compact?: boolean
}>()

const isEvent = computed<boolean>(() => props.message.kind === PatientConversationMessageKindEnum.Event)
const isInternal = computed<boolean>(
  () => props.message.visibility === PatientConversationMessageVisibilityEnum.Internal
)
const isFromPatient = computed<boolean>(() => props.message.sender?.role === PatientConversationRoleEnum.Patient)

// Staff show their role label; the patient's name stands alone (inventory override: no credentials)
const senderRole = computed<string>(() =>
  props.message.sender && !isFromPatient.value ? roleLabel(props.message.sender.role) : ""
)
const recipient = computed<string>(() => (isFromPatient.value ? "Care team" : props.patientName))

// The composer stores a page as `@[usr_…]` (same grammar as the server's mention parser); providers see the name
const MENTION_TOKEN = /@\[(usr_[A-Za-z0-9_-]+)\]/g

interface ContentSegment {
  text: string
  mention?: boolean
}

const contentSegments = computed<Array<ContentSegment>>(() => {
  const namesById = new Map((props.message.mentions ?? []).map((mention) => [mention.user.id, mention.user.full_name]))
  const segments: Array<ContentSegment> = []
  let cursor = 0
  for (const match of props.message.content.matchAll(MENTION_TOKEN)) {
    const name = namesById.get(match[1])
    if (!name) continue
    if (match.index > cursor) segments.push({ text: props.message.content.slice(cursor, match.index) })
    segments.push({ text: name, mention: true })
    cursor = match.index + match[0].length
  }
  if (cursor < props.message.content.length) segments.push({ text: props.message.content.slice(cursor) })
  return segments
})
</script>

<style scoped lang="sass">
@import "@/styles/colors"

.message-event
  display: flex
  flex-wrap: wrap
  justify-content: center
  gap: 4px 8px
  padding: 6px 12px
  font-size: 12px
  font-style: italic
  color: $grey
  text-align: center

  &__time
    white-space: nowrap

.message
  display: flex
  gap: 10px
  padding: 10px 12px
  border-radius: 8px

  // The 3px amber bar eats into the padding so note text lines up with the other messages
  &.is-internal
    padding-left: 9px
    background-color: $yellow-background
    border-left: 3px solid $yellow

  &__body
    flex: 1 1 auto
    min-width: 0

  &__header
    display: flex
    align-items: baseline
    justify-content: space-between
    gap: 12px

  &__sender
    font-size: 14px
    font-weight: 700
    color: $empty-text

  &__role, &__recipient, &__time
    font-size: 12px
    color: $grey

  &__internal-label
    display: inline-flex
    align-items: center
    gap: 4px
    margin-bottom: 4px
    font-size: 11px
    font-weight: 700
    color: $yellow
    text-transform: uppercase
    letter-spacing: 0.06em

  &__time
    flex: none
    white-space: nowrap

  &__content
    margin-top: 4px
    font-size: 14px
    line-height: 1.5
    color: $empty-text
    white-space: pre-wrap
    overflow-wrap: anywhere

  &__mention
    font-weight: 600
    color: $primary

  &__attachments
    display: flex
    flex-wrap: wrap
    gap: 6px
    margin: 8px 0 0
    padding: 0
    list-style: none

  &__read
    display: inline-flex
    align-items: center
    gap: 4px
    margin-top: 6px
    font-size: 12px
    color: $grey

  &.is-compact
    padding: 8px 10px

    &.is-internal
      padding-left: 7px
</style>
